'use client';
import React, { useState } from 'react';
import { ChevronDown, ChevronUp, X, SlidersHorizontal } from 'lucide-react';
import type { KhdaRating } from '@/components/ui/KhdaBadge';
import type { Curriculum } from '@/components/ui/CurriculumBadge';
import { DUBAI_COMMUNITIES } from '@/data/schools';

export interface FilterState {
  curricula: Curriculum[];
  khdaRatings: KhdaRating[];
  feeMin: number;
  feeMax: number;
  startingFeeMin: number;
  startingFeeMax: number;
  finalYearFeeMin: number;
  finalYearFeeMax: number;
  yearGroups: string[];
  community: string;
  busRequired: boolean;
  genderPolicy: string[];
  schoolType: string[];
  applicationStatus: string[];
  senSupport: boolean;
  maxCommuteMinutes: number;
  studentCountMax: number;
}

interface FilterSidebarProps {
  filters: FilterState;
  onChange: (filters: FilterState) => void;
  resultCount: number;
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

const CURRICULA: Curriculum[] = ['British', 'American', 'IB', 'Indian CBSE', 'Indian ICSE', 'French', 'MOE Arabic'];
const KHDA_RATINGS: KhdaRating[] = ['Outstanding', 'Very Good', 'Good', 'Acceptable', 'Weak', 'Very Weak', 'Not yet inspected'];
const YEAR_GROUPS = ['FS1', 'FS2 / KG', 'Year 1–3 / Class 1–3', 'Year 4–6 / Class 4–6', 'Year 7–9 / Class 7–9', 'Year 10–11 / Class 10', 'Year 12–13 / Class 11–12'];
const SCHOOL_TYPES = ['Private', 'Semi-Government', 'Government'];
const COMMUTE_OPTIONS = [
  { label: 'Any commute', value: 999 },
  { label: '≤ 10 min peak', value: 10 },
  { label: '≤ 15 min peak', value: 15 },
  { label: '≤ 20 min peak', value: 20 },
  { label: '≤ 25 min peak', value: 25 },
  { label: '≤ 30 min peak', value: 30 },
];

const FEE_RANGES = [
  { label: 'Under AED 10,000', min: 0, max: 10000 },
  { label: 'AED 10,000 – 20,000', min: 10000, max: 20000 },
  { label: 'AED 20,000 – 30,000', min: 20000, max: 30000 },
  { label: 'AED 30,000 – 40,000', min: 30000, max: 40000 },
  { label: 'AED 50,000 – 60,000', min: 50000, max: 60000 },
  { label: 'AED 60,000 – 70,000', min: 60000, max: 70000 },
  { label: 'AED 80,000 – 90,000', min: 80000, max: 90000 },
  { label: 'AED 90,000 – 100,000', min: 90000, max: 100000 },
  { label: 'AED 100,000+', min: 100000, max: 999999 },
];

function FilterSection({ title, children, defaultOpen = true }: { title: string; children: React.ReactNode; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-border last:border-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-3.5 px-1 text-sm font-semibold text-foreground hover:text-primary transition-colors"
      >
        {title}
        {open ? <ChevronUp size={15} className="text-muted-foreground" /> : <ChevronDown size={15} className="text-muted-foreground" />}
      </button>
      {open && <div className="pb-4 space-y-2">{children}</div>}
    </div>
  );
}

function CheckboxItem({
  label,
  checked,
  onChange,
  colorDot,
}: {
  label: string;
  checked: boolean;
  onChange: () => void;
  colorDot?: string;
}) {
  return (
    <label className="flex items-center gap-2.5 cursor-pointer group">
      <div
        onClick={onChange}
        className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-all duration-150 cursor-pointer ${
          checked ? 'bg-primary border-primary' : 'border-border group-hover:border-primary/50'
        }`}
      >
        {checked && (
          <svg width="9" height="7" viewBox="0 0 9 7" fill="none">
            <path d="M1 3.5L3.5 6L8 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </div>
      {colorDot && <span className="w-2 h-2 rounded-full" style={{ backgroundColor: colorDot }} />}
      <span className="text-sm text-foreground group-hover:text-primary transition-colors">{label}</span>
    </label>
  );
}

const khdaDotColors: Record<KhdaRating, string> = {
  Outstanding: '#6B21A8',
  'Very Good': '#166534',
  Good: '#E8622A',
  Acceptable: '#3730A3',
  Weak: '#C2410C',
  'Very Weak': '#B91C1C',
  'Not yet inspected': '#6B7280',
};

export default function FilterSidebar({ filters, onChange, resultCount, mobileOpen: externalMobileOpen, onMobileClose }: FilterSidebarProps) {
  const [internalMobileOpen, setInternalMobileOpen] = useState(false);

  // Support both internal and external open state
  const mobileOpen = externalMobileOpen !== undefined ? externalMobileOpen : internalMobileOpen;
  const closeMobile = () => {
    if (onMobileClose) onMobileClose();
    else setInternalMobileOpen(false);
  };

  const toggleCurriculum = (c: Curriculum) => {
    const next = filters.curricula.includes(c)
      ? filters.curricula.filter((x) => x !== c)
      : [...filters.curricula, c];
    onChange({ ...filters, curricula: next });
  };

  const toggleKhda = (r: KhdaRating) => {
    const next = filters.khdaRatings.includes(r)
      ? filters.khdaRatings.filter((x) => x !== r)
      : [...filters.khdaRatings, r];
    onChange({ ...filters, khdaRatings: next });
  };

  const toggleYearGroup = (yg: string) => {
    const next = filters.yearGroups.includes(yg)
      ? filters.yearGroups.filter((x) => x !== yg)
      : [...filters.yearGroups, yg];
    onChange({ ...filters, yearGroups: next });
  };

  const toggleType = (t: string) => {
    const next = filters.schoolType.includes(t)
      ? filters.schoolType.filter((x) => x !== t)
      : [...filters.schoolType, t];
    onChange({ ...filters, schoolType: next });
  };

  const setStartingFeeRange = (min: number, max: number) => {
    onChange({ ...filters, startingFeeMin: min, startingFeeMax: max });
  };

  const setFinalYearFeeRange = (min: number, max: number) => {
    onChange({ ...filters, finalYearFeeMin: min, finalYearFeeMax: max });
  };

  const resetAll = () => {
    onChange({
      curricula: [],
      khdaRatings: [],
      feeMin: 0,
      feeMax: 999999,
      startingFeeMin: 0,
      startingFeeMax: 999999,
      finalYearFeeMin: 0,
      finalYearFeeMax: 999999,
      yearGroups: [],
      community: '',
      busRequired: false,
      genderPolicy: [],
      schoolType: [],
      applicationStatus: [],
      senSupport: false,
      maxCommuteMinutes: 999,
      studentCountMax: 99999,
    });
  };

  const activeCount =
    filters.curricula.length +
    filters.khdaRatings.length +
    filters.yearGroups.length +
    filters.schoolType.length +
    (filters.community ? 1 : 0) +
    (filters.startingFeeMin > 0 || filters.startingFeeMax < 999999 ? 1 : 0) +
    (filters.finalYearFeeMin > 0 || filters.finalYearFeeMax < 999999 ? 1 : 0) +
    (filters.maxCommuteMinutes < 999 ? 1 : 0) +
    (filters.studentCountMax < 99999 ? 1 : 0);

  const sidebarContent = (
    <div className="space-y-0">
      {/* Header */}
      <div className="flex items-center justify-between pb-4 mb-1">
        <div>
          <h3 className="text-sm font-semibold text-foreground">Filters</h3>
          <p className="text-xs text-muted-foreground mt-0.5">{resultCount} schools found</p>
        </div>
        {activeCount > 0 && (
          <button
            onClick={resetAll}
            className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 font-medium transition-colors"
          >
            <X size={12} />
            Clear all ({activeCount})
          </button>
        )}
      </div>

      {/* 1. My Community — most prominent, location-led */}
      <div className="border-b border-border pb-4 mb-0">
        <div className="flex items-center gap-2 mb-2.5">
          <div className="w-5 h-5 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: '#1aaa6e' }}>
            <svg viewBox="0 0 24 24" className="w-3 h-3" fill="white" aria-hidden="true"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 7 12 7s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z"/></svg>
          </div>
          <span className="text-sm font-bold text-foreground">My Community</span>
          {filters.community && (
            <span className="ml-auto text-xs font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: '#1aaa6e18', color: '#1aaa6e', border: '1px solid #1aaa6e40' }}>
              Selected
            </span>
          )}
        </div>
        {filters.community ? (
          <div className="rounded-lg px-3 py-2.5 mb-2 flex items-center justify-between" style={{ backgroundColor: '#1aaa6e12', border: '1.5px solid #1aaa6e40' }}>
            <div className="flex items-center gap-2">
              <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 shrink-0" fill="#1aaa6e" aria-hidden="true"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 7 12 7s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z"/></svg>
              <span className="text-sm font-semibold" style={{ color: '#1aaa6e' }}>{filters.community}</span>
            </div>
            <button
              onClick={() => onChange({ ...filters, community: '' })}
              className="p-0.5 rounded hover:bg-emerald-100 transition-colors"
              title="Clear community"
            >
              <X size={13} style={{ color: '#1aaa6e' }} />
            </button>
          </div>
        ) : null}
        <select
          value={filters.community}
          onChange={(e) => onChange({ ...filters, community: e.target.value })}
          className="w-full text-sm border rounded-lg px-3 py-2 bg-white text-foreground focus:outline-none focus:ring-2 transition-all"
          style={{
            borderColor: filters.community ? '#1aaa6e' : undefined,
            focusRingColor: '#1aaa6e',
          }}
        >
          <option value="">Where do you live? Select community…</option>
          {DUBAI_COMMUNITIES.map((c) => (
            <option key={`community-opt-${c}`} value={c}>{c}</option>
          ))}
        </select>
        <p className="text-xs text-muted-foreground mt-1.5">
          Find schools closest to your community
        </p>
      </div>

      {/* 2. Maximum Commute */}
      <FilterSection title="Maximum Commute">
        <div className="space-y-1.5">
          {COMMUTE_OPTIONS.map((o) => (
            <label key={o.value} className="flex items-center gap-2.5 cursor-pointer group">
              <div
                onClick={() => onChange({ ...filters, maxCommuteMinutes: o.value })}
                className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all duration-150 cursor-pointer ${
                  filters.maxCommuteMinutes === o.value ? 'border-emerald-500' : 'border-border group-hover:border-emerald-400'
                }`}
              >
                {filters.maxCommuteMinutes === o.value && (
                  <div className="w-2 h-2 rounded-full bg-emerald-500" />
                )}
              </div>
              <span className={`text-sm transition-colors ${filters.maxCommuteMinutes === o.value ? 'font-semibold text-emerald-700' : 'text-foreground group-hover:text-emerald-700'}`}>
                {o.label}
              </span>
            </label>
          ))}
        </div>
        {filters.maxCommuteMinutes < 999 && !filters.community && (
          <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
            <svg viewBox="0 0 24 24" className="w-3 h-3 shrink-0" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>
            Select a community above to filter by commute
          </p>
        )}
      </FilterSection>

      {/* 3. Curriculum */}
      <FilterSection title="Curriculum">
        {CURRICULA.map((c) => (
          <CheckboxItem
            key={`filter-curriculum-${c}`}
            label={c}
            checked={filters.curricula.includes(c)}
            onChange={() => toggleCurriculum(c)}
          />
        ))}
      </FilterSection>

      {/* 4. KHDA Rating */}
      <FilterSection title="KHDA Rating">
        {KHDA_RATINGS.map((r) => (
          <CheckboxItem
            key={`filter-khda-${r}`}
            label={r}
            checked={filters.khdaRatings.includes(r)}
            onChange={() => toggleKhda(r)}
            colorDot={khdaDotColors[r]}
          />
        ))}
      </FilterSection>

      {/* 5. Annual Starting Fees */}
      <FilterSection title="Annual Starting Fees (AED)">
        {FEE_RANGES.map((range) => (
          <CheckboxItem
            key={`filter-startfee-${range.min}-${range.max}`}
            label={range.label}
            checked={filters.startingFeeMin === range.min && filters.startingFeeMax === range.max}
            onChange={() => setStartingFeeRange(range.min, range.max)}
          />
        ))}
      </FilterSection>

      {/* 6. Annual Final Year Fees */}
      <FilterSection title="Annual Final Year Fees (AED)">
        {FEE_RANGES.map((range) => (
          <CheckboxItem
            key={`filter-finalfee-${range.min}-${range.max}`}
            label={range.label}
            checked={filters.finalYearFeeMin === range.min && filters.finalYearFeeMax === range.max}
            onChange={() => setFinalYearFeeRange(range.min, range.max)}
          />
        ))}
      </FilterSection>

      {/* 7. Year Group */}
      <FilterSection title="Year Group / Grade" defaultOpen={false}>
        {YEAR_GROUPS.map((yg) => (
          <CheckboxItem
            key={`filter-yg-${yg}`}
            label={yg}
            checked={filters.yearGroups.includes(yg)}
            onChange={() => toggleYearGroup(yg)}
          />
        ))}
      </FilterSection>

      {/* 8. School Type */}
      <FilterSection title="School Type" defaultOpen={false}>
        {SCHOOL_TYPES.map((t) => (
          <CheckboxItem
            key={`filter-type-${t}`}
            label={t}
            checked={filters.schoolType.includes(t)}
            onChange={() => toggleType(t)}
          />
        ))}
      </FilterSection>
    </div>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <aside className="hidden lg:block w-64 xl:w-72 shrink-0">
        <div className="sticky top-20 bg-white border border-border rounded-xl p-5 shadow-sm scrollbar-thin overflow-y-auto max-h-[calc(100vh-6rem)]">
          {sidebarContent}
        </div>
      </aside>

      {/* Mobile Filter Button — only shown when not externally controlled */}
      <div className="lg:hidden">
        {externalMobileOpen === undefined && (
          <button
            onClick={() => setInternalMobileOpen(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-white border border-border rounded-xl shadow-sm text-sm font-medium text-foreground hover:bg-muted transition-all"
          >
            <SlidersHorizontal size={16} className="text-primary" />
            Filters
            {activeCount > 0 && (
              <span className="w-5 h-5 bg-primary text-white rounded-full text-xs flex items-center justify-center font-semibold">
                {activeCount}
              </span>
            )}
          </button>
        )}

        {/* Mobile Drawer */}
        {mobileOpen && (
          <div className="fixed inset-0 z-50 flex">
            <div className="absolute inset-0 bg-black/40" onClick={closeMobile} />
            <div className="relative ml-auto w-80 max-w-full h-full bg-white shadow-xl flex flex-col animate-slide-in-right">
              <div className="flex items-center justify-between p-4 border-b border-border">
                <h3 className="font-semibold text-foreground">Filter Schools</h3>
                <button onClick={closeMobile} className="p-1.5 rounded-lg hover:bg-muted transition-colors">
                  <X size={18} />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-4 scrollbar-thin">
                {sidebarContent}
              </div>
              <div className="p-4 border-t border-border">
                <button
                  onClick={closeMobile}
                  className="w-full py-3 bg-primary text-white rounded-xl font-semibold text-sm hover:bg-primary/90 transition-all active:scale-95"
                >
                  Show {resultCount} Schools
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}